import { isSafelyAppendablePathname } from 'app/utils/routes';
import { chain, Either, fold, isRight, left, right, tryCatch } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import * as t from 'io-ts';

// A string that represents a date, in the format YYYYMMDDhhmmss.
class DateStringType extends t.Type<string> {
  public constructor() {
    super(
      'DateString',
      t.string.is,
      (m, c) => {
        return pipe(
          t.string.validate(m, c),
          chain((r) => {
            return /^[0-9]{14}$/.test(r) ? t.success(r) : t.failure(r, c, 'Not a valid date string.');
          }),
        );
      },
      (a) => a,
    );
  }
}

export const dateString = new DateStringType();

export const stringEnum = <E>(enumObject: { [index: string]: unknown }, name: string): t.Type<E> => {
  const keys: { [key: string]: null } = {};

  for (const value of Object.values(enumObject)) {
    if (typeof value !== 'string') {
      throw new Error(`Enum object passed to stringEnum contains non-string value: ${value}`);
    }

    keys[value] = null;
  }

  return t.keyof(keys, name) as any;
};

class NonEmptyStringType extends t.Type<string> {
  public constructor() {
    super(
      'NonEmptyString',
      t.string.is,
      (m, c) => {
        return pipe(
          t.string.validate(m, c),
          chain((r) => {
            return r.length > 0 ? t.success(r) : t.failure(r, c, 'Empty string.');
          }),
        );
      },
      (a) => a,
    );
  }
}

export const nonEmptyString = new NonEmptyStringType();

type ErrorMessage = string;
type SafeUrl = string;

/**
 * Check to make sure that there are no search or hash parameters.
 */
const testSafelyAppendableUrl = (x: string): Either<ErrorMessage, SafeUrl> =>
  pipe(
    tryCatch(
      () => new URL(x),
      () => 'Cannot convert to a URL.',
    ),
    chain(url => {
      if (url.hash || url.search) {
        return left('URL contains hash.');
      } else if (x.match(/\/$/)) {
        return left('Contains a slash at the end.');
      } else {
        return right(x);
      }
    }),
  );

const BaseUrlStringCodecHelper: t.Type<string, string, string> = new t.Type(
  'BaseUrlStringCodecHelper',
  (v: unknown): v is string => {
    return typeof v === 'string' && isRight(testSafelyAppendableUrl(v));
  },
  (v, c) =>
    pipe(
      testSafelyAppendableUrl(v),
      fold(msg => t.failure(v, c, msg), t.success),
    ),
  v => v,
);

export const BaseUrlStringCodec: t.Type<string, string> = t.string.pipe(BaseUrlStringCodecHelper, 'BaseUrlStringCodec');

const PathnameStringCodecHelper: t.Type<string, string, string> = new t.Type(
  'PathnameStringCodecHelper',
  (v: unknown): v is string => typeof v === 'string' && isSafelyAppendablePathname(v),
  (v, c) =>
    isSafelyAppendablePathname(v) ? t.success(v) : t.failure(v, c, 'Not a safely appendable pathname.'),
  v => v,
);

// Use to prevent browsers from interpreting pathnames as fully resolvable URL.
export const PathnameStringCodec: t.Type<string, string> = t.string.pipe(
  PathnameStringCodecHelper,
  'PathnameStringCodec',
);
